import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Modal } from '../../templates/Modal';
import { usePrintComponentStore } from './store/usePrintComponentStore';
import { Button } from '../../atoms/Button';
import { Typography } from '../../molecules/Typography';
import { useParams } from 'react-router-dom';
import { useExercisePrintRequest } from './hook/useGetExercises';
import PrintDisplayOptions from './components/PrintDisplayOptions';
import AnswerSheet from '../../templates/PrintPaper/feedback';
import AditionalFilters from './components/AditionalFilters/index';
import './style.css'
import { usePrintComponentRequestStore } from './store/usePrintComponentRequest';
import { Alert } from '../../atoms/Alert';
import usePrintExercises from '../../../hooks/Print/usePrintExercises';
import { IExercisesPrint } from '../../../models/Print';
import { toast } from '../../atoms/Toast';
import { getDisciplineName } from '../../../utils/discipline';
import { getModuleLabel } from '../../../../modules/Disciplines/utils/contentHelper';
import { BrainLoadingIcon } from 'morpheus-ui';

interface IPrintComponent {
  type: 'exercise' | 'paper' | 'practice' | 'graph' | 'exerciseCronogram';
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  cancelLabel?: string;
  showCancelIcon?: boolean;
  onCancel?: () => void;
}

const NewPrintComponent: React.FC<IPrintComponent> = ({
  type,
  children,
  isOpen,
  onClose,
  title,
  subtitle,
  cancelLabel = 'Voltar',
  showCancelIcon = true,
  onCancel,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const { discipline, module } = useParams();
  const {
    hasApiCall,
    hasAnswerSheet,
    hasMoreExercises,
    numberOfColumns,
    data,
    setData,
  } = usePrintComponentStore();
  const { resetExercisePrint } = usePrintComponentRequestStore();
  const { mutate, isPending, isError } = useExercisePrintRequest();
  const { mutate: mutatePrintExercises, isPending: isPrintingExercises } =
    usePrintExercises();

  const [show, setShow] = useState<boolean>(false);

  const downloadPdf = (pdfData: Blob | string, filename: string) => {
    const triggerDownload = (url: string, revokeAfter?: () => void) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      revokeAfter?.();
    };

    if (pdfData instanceof Blob) {
      // Garante o mimetype mesmo se o servidor não tiver mandado certinho —
      // Safari/iOS é chato com download de blob sem type explícito.
      const blob =
        pdfData.type === 'application/pdf'
          ? pdfData
          : new Blob([pdfData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      triggerDownload(url, () =>
        window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
      );
      return;
    }

    if (typeof pdfData === 'string' && /^https?:\/\//i.test(pdfData)) {
      triggerDownload(pdfData);
      return;
    }

    toast({
      variant: 'error',
      title: 'Erro ao imprimir o PDF',
      message: 'Ocorreu um erro ao baixar o PDF',
      position: 'top-right',
      autoClose: 5000,
    });
  };

  const handleExercisesPrint = () => {
    if (!data || data.length === 0) return;

    const disciplineName = discipline ? getDisciplineName(discipline) : undefined;
    const moduleLabel = module ? getModuleLabel(module) : undefined;

    const slugify = (s: string) =>
      s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();

    const filenameParts = [
      'exercicios',
      disciplineName && slugify(disciplineName),
      moduleLabel && slugify(moduleLabel),
    ].filter(Boolean);
    const filename = `${filenameParts.join('-') || 'exercicios'}.pdf`;

    const body: IExercisesPrint = {
      title: title || 'Lista de Exercícios',
      subtitle: moduleLabel || subtitle || undefined,
      disciplina: disciplineName || subtitle || undefined,
      columns: numberOfColumns === 'single' ? 1 : 2,
      font_size: hasMoreExercises ? 8 : 10,
      include_gabarito: hasAnswerSheet,
      exercises: (data ?? []).map((it: any) => ({
        _id: it?._id ?? it?.id,
        slug: it?.slug,
        title: it?.title,
        statement: it?.statement ?? '',
        resolution: it?.resolution,
        alternatives: (it?.alternatives ?? []).map((alt: any) => ({
          text: alt?.text ?? '',
          correct: !!alt?.correct,
        })),
      })),
      filename,
    };

    mutatePrintExercises(body, {
      onSuccess: (pdf) => {
        downloadPdf(pdf, filename);
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Erro ao imprimir o PDF',
          message: 'Ocorreu um erro ao gerar o PDF de exercícios',
          position: 'top-right',
          autoClose: 5000,
        });
      },
    });
  };

  const buildPageStyle = () => {
    const linkEls = Array.from(document.querySelectorAll("link[rel='stylesheet']")) as HTMLLinkElement[];
    const imports = linkEls
      .filter(l => !!l.href)
      .map(l => `@import url('${l.href}');`)
      .join("\n");

    return `
      ${imports}
      @page {
        margin: 0;
        margin-top: 16px;
        margin-bottom: 8px;
      }
      @media print {
        body {
          /* Ensure background colors are printed */
          -webkit-print-color-adjust: exact; /* Chrome/Safari/Edge/Opera */
          color-adjust: exact; /* Firefox */
        }
      }
    `;
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: true,
    bodyClass: [document.documentElement.className, document.body.className].filter(Boolean).join(' '),
    pageStyle: buildPageStyle(),
    onBeforeGetContent: async () => {
      try {
        // Wait for fonts to be ready to avoid FOUT/missing fonts in print
        await (document as any)?.fonts?.ready;
      } catch {
        // Fonts API not available or failed; continue without blocking print
      }
    },
    removeAfterPrint: false,
  });

  const handleSubmit = () => {
    mutate({ subarea: module ?? "" })
  }

  const clearStep = () => {
    setShow(true);
    setData([]);
    resetExercisePrint();
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    if (hasApiCall) {
      clearStep();
      return;
    }

    onCloseGeneral();
  };

  const setButtonGroup = () => {
    if(show) {
      return (
        <div className='w-2/3 m-auto'>
          <Button
            onClick={() => handleSubmit()}
            full
            loading={isPending}
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              Continuar
            </Typography>
          </Button>
        </div>
      )
    }

    if(type === 'paper') {
      return (
        <div className='w-2/3 m-auto'>
          <Button
            onClick={handlePrint}
            full
            loading={isPending}
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              Imprimir redação
            </Typography>
          </Button>
        </div>
      )
    }

    return (
      <div className='flex w-full flex-row items-center justify-between gap-xl'>
        {type !== 'graph' && (
          <Fragment>
            <div className='flex-1'>
              <Button
                LeftIcon={showCancelIcon ? 'chevron_left' : undefined}
                iconType={showCancelIcon ? 'material' : undefined}
                variant='outlined'
                onClick={handleCancel}
                disabled={type === 'exercise' && isPrintingExercises}
                full
              >
                <Typography size='default' className='xs:text-sm md:text-md'>
                  {cancelLabel}
                </Typography>
              </Button>
            </div>
            <div className='flex-1'>
              <Typography className='text-center'>
                {data && data.length} Exercícios
              </Typography>
            </div>
          </Fragment>
        )}
        <div className='flex-1'>
          <Button
            LeftIcon='print'
            iconType='material'
            onClick={type === 'exercise' ? handleExercisesPrint : handlePrint}
            loading={type === 'exercise' ? isPrintingExercises : false}
            full
          >
            <Typography
              size='default'
              className='text-white xs:text-sm md:text-md'
            >
              {type === 'exercise' && isPrintingExercises
                ? 'Gerando seu PDF'
                : 'Imprimir lista'}
            </Typography>
          </Button>
        </div>
      </div>
    )
  }

  const onCloseGeneral = () => {
    if (type === 'exercise' && isPrintingExercises) return;
    onClose();
  }

  useEffect(() => {
    if(isError) {
      console.log('Error');
    }
  }, [isPending])

  useEffect(() => {
    if(hasApiCall) {
      setData([]);
      setShow(true)
    }
  }, [hasApiCall])

  useEffect(() => {
    if(hasApiCall && data.length > 0) {
      setShow(false)
    }
  }, [data])

  useEffect(() => {
    if (isOpen && !hasApiCall) {
      setShow(false);
    }
  }, [isOpen, hasApiCall]);

  return (
    <Modal
      position="top"
      size='md'
      isOpen={isOpen}
      onClose={() => onCloseGeneral()}
      className='xs:w-[90%] md:w-[70%] lg:w-[60%]'
      title={title}
      subtitle={subtitle}
    >
      <div className='flex flex-col gap-xl bg-color-lightest'>
        {type === 'practice' && <Alert
          message={
            <Typography size='default' className='text-card-color-negative'>
              Para imprimir com o gabarito, finalize o simulado.
            </Typography>
          }
          variant='warning'
        />}
        {hasApiCall && show && !isPrintingExercises && <AditionalFilters disabled={data?.length > 0} subject={title ?? "-"} slug={module}/>}
        {type === 'exercise' && !isPrintingExercises && <PrintDisplayOptions />}
        {type === 'exercise' && isPrintingExercises ? (
          <div className='flex h-[60vh] flex-col items-center justify-center gap-md rounded-lg border-hairline border-mono-color-lighter bg-color-lightest px-6 text-center'>
            <BrainLoadingIcon className='h-24 w-24 animate-pulse stroke-brand-color-primary-pure stroke-2' />
            <Typography type='title' size='h5' className='text-brand-color-primary-pure'>
              Gerando seu PDF...
            </Typography>
            {data && data.length > 100 ? (
              <Fragment>
                <Typography size='default' className='max-w-md text-mono-color-darkest'>
                  Estamos preparando uma lista com <strong>{data.length} exercícios</strong>. Esse processo pode levar <strong>até 1 minuto</strong> — quanto mais exercícios, mais tempo levamos para montar o material.
                </Typography>
                <Typography size='small' className='max-w-md text-mono-color-darker'>
                  Não feche esta janela. Assim que estiver pronto, o download do PDF começará automaticamente.
                </Typography>
              </Fragment>
            ) : (
              <Fragment>
                <Typography size='default' className='max-w-md text-mono-color-darkest'>
                  Estamos preparando seu material. A geração da lista pode levar <strong>até 1 minuto</strong>.
                </Typography>
                <Typography size='small' className='max-w-md text-mono-color-darker'>
                  Não feche esta janela. O download do PDF começará assim que estiver pronto.
                </Typography>
              </Fragment>
            )}
          </div>
        ) : (
          !show && (
            <div id="paperContainer" className='h-[60vh] overflow-auto rounded-lg border-hairline border-mono-color-lighter bg-[#fff]'>
              <div ref={componentRef} className="w-full h-fit">
                {children}
              </div>
            </div>
          )
        )}
        {/*{hasAnswerSheet && !show && <AnswerSheet /> }*/}
        {setButtonGroup()}
      </div>
    </Modal>
  );
}

export default NewPrintComponent;
